/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Video,
  Headphones,
  Users,
  BookOpen,
  Heart,
  DollarSign,
  Sparkles,
  MessageCircle,
  Mic,
  ExternalLink,
  Brain,
  Dumbbell,
  Compass,
  Landmark,
  ArrowRight,
  Check,
  Flame,
  CalendarCheck,
  Quote,
} from 'lucide-react';
import PortalSubPage, { ComingSoonCard } from '../PortalSubPage';
import {
  NEXT_MEETING,
  INTEREST_MAILTO,
  INTEREST_EMAIL,
  CIRCLE_FOCUS,
  CIRCLE_GUEST_SPEAKER,
  CIRCLE_TOPICS,
  CIRCLE_PRICE,
  getReflectionForOffset,
} from '../../data/portalData';

const PILLARS = [
  {
    key: 'mind',
    icon: Brain,
    eyebrow: 'Pillar 01',
    title: 'Mind',
    lede: 'A clear head is a leader\u2019s greatest asset. Reset the noise, sharpen the focus, and choose the thoughts you keep.',
    prompts: [
      'What thought have I been renting space to this week that isn\u2019t serving me?',
      'Where do I need to say "no" so I can protect my focus?',
      'Who do I need to talk to before this weight compounds?',
    ],
    linkLabel: 'Open Meditation',
    linkHref: '/portal/meditation',
  },
  {
    key: 'body',
    icon: Dumbbell,
    eyebrow: 'Pillar 02',
    title: 'Body',
    lede: 'The body keeps the score. Move it, breathe with it, listen when it whispers \u2014 before it screams.',
    prompts: [
      'How many hours did I actually sleep this week?',
      'What did I eat that I know cost me energy?',
      'When was my last honest workout, walk, or breathwork session?',
    ],
    linkLabel: 'Open Health & Wellness',
    linkHref: '/portal/health-wellness',
  },
  {
    key: 'purpose',
    icon: Compass,
    eyebrow: 'Pillar 03',
    title: 'Purpose',
    lede: 'Purpose isn\u2019t found in a weekend \u2014 it\u2019s built in the small decisions that align your work with your gifts.',
    prompts: [
      'What am I building that only I can build?',
      'Is my work matching my gifts \u2014 or draining them?',
      'What is one bold move I keep postponing?',
    ],
    linkLabel: 'Open Coaching',
    linkHref: '/portal/coaching',
  },
  {
    key: 'finances',
    icon: DollarSign,
    eyebrow: 'Pillar 04',
    title: 'Finances',
    lede: 'Money is a tool. What you build with it says everything about who you are and who you\u2019re building for.',
    prompts: [
      'Do I have a retirement plan I actually understand?',
      'How much did I invest this month \u2014 not just save?',
      'What is one debt I can attack aggressively in the next 90 days?',
    ],
    linkLabel: 'Open Financial Wellness',
    linkHref: '/portal/financial-wellness',
  },
  {
    key: 'legacy',
    icon: Landmark,
    eyebrow: 'Pillar 05',
    title: 'Legacy',
    lede: 'Legacy is what happens when you refuse to be the last of your kind. Build something that outlives the applause.',
    prompts: [
      'Who am I mentoring \u2014 formally or informally \u2014 right now?',
      'What conversation with family have I been avoiding?',
      'What one thing would I want said about me in a room I can\u2019t enter?',
    ],
    linkLabel: 'Open Community',
    linkHref: '/portal/community',
  },
];

// -------- 5 Pillars progress hook (localStorage) --------
const PILLAR_LOG_KEY = 'reset_pillar_log_v1';

function isoWeekKey(date = new Date()) {
  // ISO week number (YYYY-WW), Monday-based, per ISO 8601.
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function prevWeekKey(weekKey) {
  const [y, w] = weekKey.split('-W').map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const dayNum = jan4.getUTCDay() || 7;
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - (dayNum - 1));
  const target = new Date(week1Mon);
  target.setUTCDate(week1Mon.getUTCDate() + (w - 1) * 7 - 7); // go back 1 week
  return isoWeekKey(target);
}

function readLog() {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(PILLAR_LOG_KEY) || '{}');
  } catch (_e) {
    return {};
  }
}

function writeLog(log) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(PILLAR_LOG_KEY, JSON.stringify(log));
  }
}

// Milestone messages: pick the highest milestone reached and render its warm one-liner.
const PILLAR_MILESTONES = [
  {
    at: 1,
    icon: Sparkles,
    label: 'You showed up',
    line: 'One down, brother \u2014 that\u2019s already more honest than yesterday.',
  },
  {
    at: 3,
    icon: Sparkles,
    label: 'Three deep',
    line: 'Three answered. You\u2019re not fixing everything \u2014 you\u2019re just choosing to see.',
  },
  {
    at: 5,
    icon: Flame,
    label: 'Halfway home',
    line: 'Five in. You\u2019re building the muscle to sit with hard questions.',
  },
  {
    at: 8,
    icon: Flame,
    label: 'Locked in',
    line: 'Eight down \u2014 this is the work most brothers skip. Keep going.',
  },
  {
    at: 12,
    icon: Flame,
    label: 'Almost there',
    line: 'Twelve honest answers. This is what a reset actually looks like.',
  },
  {
    at: 15,
    icon: Sparkles,
    label: 'All five pillars',
    line: 'Every prompt answered. Text this week to a brother who needs the same reset.',
  },
];

function getMilestone(count) {
  let match = null;
  for (const m of PILLAR_MILESTONES) {
    if (count >= m.at) match = m;
  }
  return match;
}

function usePillarProgress() {  const [log, setLog] = useState(() => readLog());
  const week = useMemo(() => isoWeekKey(), []);

  const toggle = (pillarKey, promptIdx, note) => {
    setLog((prev) => {
      const next = { ...prev };
      const weekMap = { ...(next[week] || {}) };
      const pillarMap = { ...(weekMap[pillarKey] || {}) };
      const id = String(promptIdx);
      if (pillarMap[id]) {
        delete pillarMap[id];
      } else {
        pillarMap[id] = {
          answered_at: new Date().toISOString(),
          note: (note || '').trim() || null,
        };
      }
      if (Object.keys(pillarMap).length) {
        weekMap[pillarKey] = pillarMap;
      } else {
        delete weekMap[pillarKey];
      }
      if (Object.keys(weekMap).length) {
        next[week] = weekMap;
      } else {
        delete next[week];
      }
      writeLog(next);
      return next;
    });
  };

  const saveNote = (pillarKey, promptIdx, note) => {
    setLog((prev) => {
      const next = { ...prev };
      const weekMap = { ...(next[week] || {}) };
      const pillarMap = { ...(weekMap[pillarKey] || {}) };
      const id = String(promptIdx);
      pillarMap[id] = {
        answered_at:
          pillarMap[id]?.answered_at || new Date().toISOString(),
        note: (note || '').trim() || null,
      };
      weekMap[pillarKey] = pillarMap;
      next[week] = weekMap;
      writeLog(next);
      return next;
    });
  };

  const isAnswered = (pillarKey, promptIdx) =>
    Boolean(log[week]?.[pillarKey]?.[String(promptIdx)]);

  const noteFor = (pillarKey, promptIdx) =>
    log[week]?.[pillarKey]?.[String(promptIdx)]?.note || '';

  const answeredThisWeek = useMemo(() => {
    const weekMap = log[week] || {};
    return Object.values(weekMap).reduce((sum, m) => sum + Object.keys(m).length, 0);
  }, [log, week]);

  const totalPrompts = useMemo(
    () => PILLARS.reduce((sum, p) => sum + p.prompts.length, 0),
    [],
  );

  // Streak = number of consecutive weeks (ending this week or last week) with >=1 entry.
  const streak = useMemo(() => {
    let count = 0;
    let cursor = week;
    // If nothing this week, start counting from last week so a fresh Monday doesn't zero out.
    if (!log[cursor] || Object.keys(log[cursor]).length === 0) {
      cursor = prevWeekKey(cursor);
    }
    while (log[cursor] && Object.keys(log[cursor]).length > 0) {
      count += 1;
      cursor = prevWeekKey(cursor);
    }
    return count;
  }, [log, week]);

  const resetThisWeek = () => {
    setLog((prev) => {
      const next = { ...prev };
      delete next[week];
      writeLog(next);
      return next;
    });
  };

  return {
    week,
    isAnswered,
    noteFor,
    toggle,
    saveNote,
    answeredThisWeek,
    totalPrompts,
    streak,
    resetThisWeek,
  };
}

function PromptRow({ pillar, promptIdx, prompt, pillarIdx, progress }) {
  const answered = progress.isAnswered(pillar.key, promptIdx);
  const savedNote = progress.noteFor(pillar.key, promptIdx);
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(savedNote);

  useEffect(() => {
    setNote(savedNote);
  }, [savedNote]);

  const handleToggle = () => {
    progress.toggle(pillar.key, promptIdx, note);
    if (answered) setExpanded(false);
  };

  return (
    <li
      className={`rounded-xl border p-4 transition-colors ${
        answered
          ? 'bg-[#D4AF37]/8 border-[#D4AF37]/40'
          : 'bg-[#0A0A0A]/60 border-white/10 hover:border-white/20'
      }`}
      data-testid={`prompt-row-${pillar.key}-${promptIdx}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={handleToggle}
          aria-pressed={answered}
          aria-label={answered ? 'Mark unanswered' : 'Mark answered'}
          className={`mt-0.5 w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
            answered
              ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
              : 'border-white/25 text-transparent hover:border-[#D4AF37]/60'
          }`}
          data-testid={`prompt-toggle-${pillar.key}-${promptIdx}`}
        >
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-[#D4AF37] font-cormorant text-sm mr-2">
                {String(pillarIdx + 1).padStart(2, '0')}.{promptIdx + 1}
              </span>
              <span className="text-zinc-200 font-manrope text-sm md:text-base leading-relaxed">
                {prompt}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-zinc-500 hover:text-[#D4AF37] font-manrope text-xs uppercase tracking-widest whitespace-nowrap"
              data-testid={`prompt-note-toggle-${pillar.key}-${promptIdx}`}
            >
              {expanded ? 'Hide' : savedNote ? 'Edit note' : 'Add note'}
            </button>
          </div>

          {!expanded && savedNote && (
            <p className="text-zinc-400 font-manrope text-sm italic mt-2 pl-1 border-l-2 border-[#D4AF37]/40 pl-3">
              &ldquo;{savedNote}&rdquo;
            </p>
          )}

          {expanded && (
            <div className="mt-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Between us. Write what you actually think."
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white font-manrope text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#D4AF37]/50"
                data-testid={`prompt-note-input-${pillar.key}-${promptIdx}`}
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    progress.saveNote(pillar.key, promptIdx, note);
                    setExpanded(false);
                  }}
                  className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-1.5 rounded-full font-manrope text-xs font-semibold hover:bg-amber-200 transition-colors"
                  data-testid={`prompt-note-save-${pillar.key}-${promptIdx}`}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNote(savedNote);
                    setExpanded(false);
                  }}
                  className="text-zinc-500 hover:text-white font-manrope text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export function PortalPillars() {
  const progress = usePillarProgress();
  const pct = Math.round((progress.answeredThisWeek / progress.totalPrompts) * 100);
  const weekLabel = (() => {
    const [y, w] = progress.week.split('-W');
    return `Week ${w} \u00b7 ${y}`;
  })();

  return (
    <PortalSubPage
      eyebrow="The 5 Pillars"
      title={
        <>
          Track your <span className="italic text-[#D4AF37]">reset.</span>
        </>
      }
      intro={"Mind. Body. Purpose. Finances. Legacy. These are the five rooms every brother walks through. Answer honestly, keep the streak, come back next week."}
      icon={Sparkles}
    >
      {/* Progress dashboard */}
      <div
        className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 via-[#8C6239]/10 to-transparent border border-[#D4AF37]/30 p-6 md:p-8"
        data-testid="pillars-progress-dashboard"
      >
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-2">
              Your reset &middot; {weekLabel}
            </p>
            <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-1">
              <span data-testid="pillars-answered-count">{progress.answeredThisWeek}</span>
              <span className="text-zinc-500"> / {progress.totalPrompts}</span>
              <span className="text-zinc-400 font-manrope text-base md:text-lg font-normal ml-2">
                answered this week
              </span>
            </h2>
            <div
              className="mt-4 h-2 w-full max-w-md rounded-full bg-white/10 overflow-hidden"
              aria-label="Weekly progress"
            >
              <div
                className="h-full bg-[#D4AF37] transition-all duration-500"
                style={{ width: `${pct}%` }}
                data-testid="pillars-progress-bar"
              />
            </div>
          </div>
          <div
            className="flex items-center gap-3 bg-[#0A0A0A]/60 border border-[#D4AF37]/25 rounded-xl px-4 py-3"
            data-testid="pillars-streak"
          >
            <Flame className="w-6 h-6 text-[#D4AF37]" />
            <div>
              <p className="text-zinc-400 font-manrope text-[10px] uppercase tracking-widest">
                Streak
              </p>
              <p className="text-white font-cormorant text-2xl font-medium leading-none">
                <span data-testid="pillars-streak-count">{progress.streak}</span>
                <span className="text-zinc-500 font-manrope text-sm font-normal ml-1">
                  week{progress.streak === 1 ? '' : 's'}
                </span>
              </p>
            </div>
          </div>
        </div>
        {progress.answeredThisWeek > 0 && (
          <button
            type="button"
            onClick={progress.resetThisWeek}
            className="mt-5 text-zinc-500 hover:text-red-400 font-manrope text-xs uppercase tracking-widest"
            data-testid="pillars-reset-week"
          >
            Reset this week
          </button>
        )}
        {(() => {
          const milestone = getMilestone(progress.answeredThisWeek);
          if (!milestone) return null;
          const MIcon = milestone.icon;
          return (
            <div
              className="mt-6 rounded-xl bg-[#0A0A0A]/70 border border-[#D4AF37]/40 p-4 md:p-5 flex items-start gap-4"
              data-testid="pillars-nudge"
              role="status"
              aria-live="polite"
            >
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                <MIcon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.3em] font-bold mb-1"
                  data-testid="pillars-nudge-label"
                >
                  {milestone.label}
                </p>
                <p
                  className="text-white font-cormorant text-lg md:text-xl leading-snug"
                  data-testid="pillars-nudge-line"
                >
                  {milestone.line}
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Overview strip */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3"
        data-testid="pillars-overview"
      >
        {PILLARS.map((p) => {
          const Icon = p.icon;
          const answeredInPillar = p.prompts.reduce(
            (n, _q, i) => n + (progress.isAnswered(p.key, i) ? 1 : 0),
            0,
          );
          const complete = answeredInPillar === p.prompts.length;
          return (
            <a
              key={p.key}
              href={`#pillar-${p.key}`}
              className={`group rounded-xl border p-4 transition-colors ${
                complete
                  ? 'bg-[#D4AF37]/10 border-[#D4AF37]/50'
                  : 'bg-[#111111] border-white/10 hover:border-[#D4AF37]/50'
              }`}
              data-testid={`pillar-chip-${p.key}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-colors">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <span
                  className={`font-manrope text-[10px] font-bold rounded-full px-2 py-0.5 ${
                    complete
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'bg-white/5 text-zinc-500'
                  }`}
                  data-testid={`pillar-chip-count-${p.key}`}
                >
                  {answeredInPillar}/{p.prompts.length}
                </span>
              </div>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest mb-1">
                {p.eyebrow}
              </p>
              <p className="font-cormorant text-xl font-medium text-white leading-none">
                {p.title}
              </p>
            </a>
          );
        })}
      </div>

      {/* Detail cards */}
      {PILLARS.map((p, idx) => {
        const Icon = p.icon;
        return (
          <div
            key={p.key}
            id={`pillar-${p.key}`}
            className="rounded-2xl bg-[#111111] border border-white/10 p-6 md:p-8 scroll-mt-24"
            data-testid={`pillar-card-${p.key}`}
          >
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-2">
                  {p.eyebrow}
                </p>
                <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white leading-tight mb-3">
                  {p.title}
                </h2>
                <p className="text-zinc-300 font-manrope text-base md:text-lg leading-relaxed mb-6 max-w-2xl">
                  {p.lede}
                </p>

                <div className="flex items-center gap-2 mb-3">
                  <CalendarCheck className="w-4 h-4 text-[#D4AF37]" />
                  <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest font-bold">
                    Ask yourself &middot; mark what you sat with
                  </p>
                </div>
                <ul className="space-y-3 mb-6">
                  {p.prompts.map((q, i) => (
                    <PromptRow
                      key={i}
                      pillar={p}
                      pillarIdx={idx}
                      promptIdx={i}
                      prompt={q}
                      progress={progress}
                    />
                  ))}
                </ul>

                <Link
                  to={p.linkHref}
                  className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm font-semibold hover:text-amber-200 transition-colors"
                  data-testid={`pillar-cta-${p.key}`}
                >
                  {p.linkLabel} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        );
      })}

      {/* Closing note */}
      <div className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/15 via-[#111111] to-[#111111] border border-[#D4AF37]/30 p-6 md:p-8">
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-2">
          One line at a time
        </p>
        <h3 className="font-cormorant text-2xl md:text-3xl font-medium text-white mb-3">
          You don&rsquo;t have to fix all five today.
        </h3>
        <p className="text-zinc-300 font-manrope text-base leading-relaxed max-w-2xl">
          Pick the pillar you&rsquo;ve been dodging. Answer one question honestly. Come back next week and keep the streak alive.
        </p>
      </div>
    </PortalSubPage>
  );
}
